import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import {
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreateServerDialog from './CreateServerDialog';
import { GuardrailsService } from 'services/GuardrailsService';
import { Utils } from 'shared/helpers';
import { FormikHelpers } from 'formik';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

export interface IServer {
  _id: string;
  name: string;
  description: string;
  endpoint: string;
  status: string;
  isActive: boolean;
  createdBy: string;
  updatedBy: any;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const ServerList = (): ReactElement => {
  const [openDeleteServer, setOpenDeleteServer] = useState(false);
  const [serverCreate, setServerCreate] = useState(false);
  const [serverLists, setServerLists] = useState<IServer[]>([]);
  const intialValues = {
    name: '',
    endpoint: '',
    description: '',
  };
  const [serverForm, setServerForm] = useState({ ...intialValues });
  const [selectedServer, setSelectedServer] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  // API Calls
  const getServerLists = (): void => {
    setIsLoading(true);
    GuardrailsService.getserverList()
      .then((res) => {
        const { servers } = res.data.data;
        setServerLists(servers);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const handleDeleteServer = (): void => {
    GuardrailsService.deleteServer(selectedServer)
      .then((res) => {
        Utils.handleSuccessResponse(res);
        setIsLoading(true);
        getServerLists();
        setIsLoading(false);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setSelectedServer('');
      });
  };

  const actionOnSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    if (selectedServer) {
      GuardrailsService.updateServer(selectedServer, formValues)
        .then((res) => {
          setServerCreate(false);
          Utils.handleSuccessResponse(res);
          getServerLists();
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        })
        .finally(() => {
          formik.resetForm();
          formik.setSubmitting(false);
          setSelectedServer('');
        });
    } else {
      GuardrailsService.createServer(formValues)
        .then((res) => {
          setServerCreate(false);
          Utils.handleSuccessResponse(res);
          getServerLists();
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        })
        .finally(() => {
          formik.resetForm();
          formik.setSubmitting(false);
        });
    }
  };

  const handleCloseDeleteServer = (): void => {
    setOpenDeleteServer(false);
  };

  const actionOnEdit = (server: IServer): void => {
    setServerForm({
      ...serverForm,
      name: server.name,
      endpoint: server.endpoint,
      description: server.description,
    });
    setServerCreate(true);
  };

  useEffect(() => {
    getServerLists();
  }, []);

  // UI Elements
  return (
    <>
      <PageHeader
        noPadding
        title="Upstream MCP Servers"
        description="Register and manage the MCP servers your proxy forwards to."
        actions={
          <>
            {orgACLConfig.policies?.includes(
              'policies.guardrails.servers.create'
            ) && (
              <AppPrimaryButton
                className="btn-small"
                onClick={() => {
                  setServerForm(intialValues);
                  setServerCreate(true);
                }}
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                }
                title="Add Server"
              />
            )}
          </>
        }
      />

      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && serverLists.length > 0 && (
        <TableContainer>
          <Table
            className="app-table"
            sx={{ minWidth: 650 }}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Active</TableCell>
                {(orgACLConfig.policies?.includes(
                  'policies.guardrails.servers.edit'
                ) ||
                  orgACLConfig.policies?.includes(
                    'policies.guardrails.servers.delete'
                  )) && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {serverLists.map((server: IServer) => (
                <TableRow>
                  <TableCell>{server.name}</TableCell>
                  <TableCell>{server.endpoint}</TableCell>
                  <TableCell>
                    <Chip
                      label="Unknown"
                      sx={{ background: 'white', color: 'black' }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {server.isActive ? (
                      <Chip
                        label="Active"
                        sx={{ background: 'black', color: 'white' }}
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Blocked"
                        sx={{ background: 'red', color: 'white' }}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {orgACLConfig.policies?.includes(
                      'policies.guardrails.servers.edit'
                    ) && (
                      <IconButton
                        onClick={() => {
                          setSelectedServer(server._id);
                          actionOnEdit(server);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {orgACLConfig.policies?.includes(
                      'policies.guardrails.servers.delete'
                    ) && (
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedServer(server._id);
                          setOpenDeleteServer(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && serverLists.length === 0 && (
        <Box className="app-plain-card empty-content">
          <EmptyPlaceholder
            icon={<DnsOutlinedIcon fontSize="large" />}
            title="No servers registered yet. Add your first upstream MCP server."
          />
        </Box>
      )}

      <ConfirmDialog
        title="Delete Server"
        subTitle="This will permanently remove this server and its tool allowlists. API keys referencing this server will no longer be able to route traffic to it."
        showConfirm={openDeleteServer}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteServer();
          handleDeleteServer();
        }}
        onCancel={handleCloseDeleteServer}
        onCloseDialog={handleCloseDeleteServer}
      />
      {serverCreate && (
        <CreateServerDialog
          open={serverCreate}
          onCloseServer={() => setServerCreate(false)}
          serverForm={serverForm}
          actionOnSubmit={actionOnSubmit}
        />
      )}
    </>
  );
};

export default ServerList;
